import { Printer, Download } from 'lucide-react';
import { useMemo, useState } from 'react';
import {
  useBalanceSheet,
  useIncomeStatement,
} from '@/hooks/react-query/useGeneralLedger';
import { useFund } from '@/hooks/react-query/useFunds';
import { Button } from '../ui/button';
import { StatementOfFinancialPosition } from './StatementOfFinancialPosition';
import { StatementOfOperations } from './StatementOfOperations';
import { DrawdownService } from '@/services/drawdowns/drawdownService';
import { toast } from 'sonner';
import { formatDate } from '@/utils/formatters';

interface FinancialStatementsTabProps {
  fundId: string;
}

export function FinancialStatementsTab({
  fundId,
}: FinancialStatementsTabProps) {
  const fundQuery = useFund(fundId);

  const balanceSheetParams = useMemo(
    () => ({
      fund_id: fundId,
    }),
    [fundId]
  );

  const incomeStatementParams = useMemo(
    () => ({
      fund_id: fundId,
    }),
    [fundId]
  );

  const balanceSheetQuery = useBalanceSheet(balanceSheetParams);
  const incomeStatementQuery = useIncomeStatement(incomeStatementParams);

  const [isExporting, setIsExporting] = useState(false);

  const handlePrint = () => {
    window.print();
  };

  const handleExportPDF = async () => {
    setIsExporting(true);
    try {
      await DrawdownService.downloadFinancialStatementsPDF(
        fundId,
        balanceSheetQuery.data?.as_of_date
      );
      toast.success('Financial Statements PDF downloaded');
    } catch (error) {
      console.error('Failed to export PDF:', error);
      toast.error('Failed to export PDF');
    } finally {
      setIsExporting(false);
    }
  };

  const fundName = fundQuery.data?.name || 'Fund';
  const asOfDate = balanceSheetQuery.data?.as_of_date
    ? formatDate(balanceSheetQuery.data.as_of_date)
    : formatDate(new Date());
  // The API serializes Decimal totals as JSON strings; coerce to a number so
  // downstream arithmetic adds instead of string-concatenating.
  const netIncome = Number(incomeStatementQuery.data?.totals.net_income ?? 0);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-start">
        <div className="flex-1" />
        <div className="text-center flex-1">
          <h1 className="text-2xl font-bold tracking-wide">
            FINANCIAL STATEMENTS
          </h1>
          <p className="text-muted-foreground mt-1">
            {fundName} • As of {asOfDate}
          </p>
        </div>
        <div className="flex gap-2 flex-1 justify-end">
          <Button variant="outline" size="sm" onClick={handlePrint}>
            <Printer className="h-4 w-4 mr-2" />
            Print
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={handleExportPDF}
            disabled={isExporting}
          >
            <Download className="h-4 w-4 mr-2" />
            {isExporting ? 'Exporting...' : 'Export PDF'}
          </Button>
        </div>
      </div>

      {/* Statement of Operations (Income Statement) */}
      <StatementOfOperations
        incomeStatement={incomeStatementQuery.data}
        isLoading={incomeStatementQuery.isLoading}
      />

      {/* Statement of Financial Position (Balance Sheet) */}
      <StatementOfFinancialPosition
        balanceSheet={balanceSheetQuery.data}
        netIncome={netIncome}
        isLoading={balanceSheetQuery.isLoading}
      />
    </div>
  );
}
